import { forwardRef } from "react";

import { Box, BoxProps, useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

export type FormBlockProps = BoxProps & {
  variant?: "inline" | "card" | "elevated-card";
  fullWidth?: boolean;
};

export const FormBlock = forwardRef<HTMLDivElement, FormBlockProps>(
  ({ variant = "inline", sx, children, fullWidth = false, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.block) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        boxSizing: "border-box",
        ...theme.typography.body2,
        color: theme.palette.formsComponents.block.textColor,
        ...((variant === "card" || variant === "elevated-card") && {
          padding: 3,
          backgroundColor: theme.palette.formsComponents.block.cardBackgroundColor,
          border: `1px solid ${theme.palette.formsComponents.block.cardBorderColor}`,
          borderRadius: theme.radius(1),
        }),
        ...(variant === "elevated-card" && {
          boxShadow: `2px 2px 24px rgba(0, 0, 0, 0.06)`,
        }),
        ...(fullWidth && {
          width: "100%",
        }),
      },
      sx
    );

    return (
      <Box ref={ref} {...rest} sx={styles}>
        {children}
      </Box>
    );
  }
);

FormBlock.displayName = "FormBlock";
